/**
 * Параметры размера картинки для конкретной модели генерации.
 *
 * Все картинки в продукте идут через семейство Seedream 5 Pro. У WaveSpeed
 * два endpoint'а одной модели: text-to-image без референсов и edit с ними.
 * Пикселей Seedream не принимает — только соотношение сторон из закрытого
 * списка и ступень разрешения. Поэтому перевод «наш `ImageSpec` → параметры
 * запроса» живёт здесь, рядом с расчётом размеров, а не размазан по сервисам.
 *
 * Проверено обращением к API 27.08.2026: неизвестные поля модель молча
 * игнорирует, поэтому лишнего не отправляем — иначе журнал обращений будет
 * врать, что мы просили размер, которого модель не видела.
 */
/** Text-to-image без референсов. */
export const SEEDREAM_V5_PRO_MODEL_ID = 'bytedance/seedream-v5.0-pro';
/** Image-edit с референсами (`images`). */
export const SEEDREAM_V5_PRO_EDIT_MODEL_ID = 'bytedance/seedream-v5.0-pro/edit';
/** Image-to-video для анимации разворотов. */
export const WAN_3_0_IMAGE_TO_VIDEO_MODEL_ID = 'alibaba/wan-3.0/image-to-video';
/** Модель по наличию референсов — одна семья, разные endpoint'ы WaveSpeed. */
export function seedreamV5ProModelId(hasReferences) {
    return hasReferences ? SEEDREAM_V5_PRO_EDIT_MODEL_ID : SEEDREAM_V5_PRO_MODEL_ID;
}
/**
 * Снятые с эксплуатации модели и их замена.
 *
 * Модель хранится в `book.attributes` каждой книги — снимок дефолтов на момент
 * создания. Выбора модели в интерфейсе нет, поэтому смена модели продукта
 * обязана догонять уже созданные книги: иначе книга, заведённая вчера,
 * навсегда останется на Flux, а новый дефолт увидят только новые книги.
 */
const RETIRED_MODEL_IDS = Object.freeze({
    'wavespeed-ai/flux-dev': SEEDREAM_V5_PRO_EDIT_MODEL_ID,
    'wavespeed-ai/flux-2-dev/text-to-image': SEEDREAM_V5_PRO_MODEL_ID,
    'bytedance/seedream-v4/edit': SEEDREAM_V5_PRO_EDIT_MODEL_ID,
    'wavespeed-ai/wan-2.1/image-to-video': WAN_3_0_IMAGE_TO_VIDEO_MODEL_ID,
});
/** Актуальный id модели: снятую с эксплуатации заменяет, остальные не трогает. */
export function currentModelId(modelId) {
    return RETIRED_MODEL_IDS[modelId] ?? modelId;
}
/**
 * «Пропорции решает модель» — соотношение сторон не отправляется вовсе.
 *
 * Значения `auto` у провайдера нет: проверено обращением к API 27.08.2026,
 * `auto`, `match_input_image` и `null` отвергаются с 400. Единственный способ
 * не задавать пропорции — не слать поле; для edit-моделей это означает
 * «как у входной картинки».
 */
export const AUTO_ASPECT_RATIO = 'auto';
/** Ступени разрешения Seedream 5 (`resolution`). */
export const IMAGE_RESOLUTION_TIERS = ['1k', '1.5k', '2k'];
/** Соотношения сторон, которые принимает Seedream 5 (`aspect_ratio`). */
export const SEEDREAM_ASPECT_RATIOS = [
    '1:1',
    '1:2',
    '2:1',
    '1:3',
    '3:1',
    '2:3',
    '3:2',
    '3:4',
    '4:3',
    '4:5',
    '5:4',
    '9:16',
    '16:9',
    '9:21',
    '21:9',
];
/**
 * Ближайшее допустимое соотношение сторон.
 *
 * Сравнение по логарифму отношения, а не по разности: так «в полтора раза
 * шире» и «в полтора раза уже» отстоят от квадрата одинаково, и выбор
 * не перекашивает в сторону широких форматов.
 *
 * Ничьи бывают: 1.414 (лист A-формата) стоит ровно посередине между `4:3`
 * и `3:2`. Выигрывает менее вытянутый вариант — обрезать придётся меньше.
 * Правило явное, потому что иначе исход зависел бы от порядка в списке.
 */
export function nearestAspectRatio(size, allowed = SEEDREAM_ASPECT_RATIOS) {
    const target = Math.log(size.widthPx / size.heightPx);
    let best = allowed[0];
    let bestDistance = Number.POSITIVE_INFINITY;
    let bestExtremity = Number.POSITIVE_INFINITY;
    for (const candidate of allowed) {
        const [width, height] = candidate.split(':').map(Number);
        const ratio = width / height;
        const distance = Math.abs(Math.log(ratio) - target);
        const extremity = Math.abs(Math.log(ratio));
        const closer = distance < bestDistance - 1e-9;
        const tieButCalmer = Math.abs(distance - bestDistance) <= 1e-9 && extremity < bestExtremity;
        if (closer || tieButCalmer) {
            best = candidate;
            bestDistance = distance;
            bestExtremity = extremity;
        }
    }
    return best;
}
/** Модель из семейства Seedream 5 — и `pro`, и `lite`, и `edit`, и text-to-image. */
export function usesAspectRatio(modelId) {
    return /seedream-v5/iu.test(modelId);
}
/**
 * Параметры запроса к модели картинки.
 *
 * Seed уходит в запрос только тем моделям, которые его понимают. На выбор
 * ключа идемпотентности это не влияет: там seed учитывается отдельным полем,
 * поэтому «перегенерировать» по-прежнему даёт новую задачу.
 */
/**
 * Что именно мы попросили по размеру — для журнала обращений (§3.11.1).
 *
 * Пиксели туда писать нельзя, если модель их не видела: иначе журнал
 * утверждает, что мы просили 1024×1448, тогда как в запрос ушло `3:2` и `1k`.
 */
export function requestedImageSize(input) {
    if (usesAspectRatio(input.modelId)) {
        return {
            aspect_ratio: input.aspectRatio === AUTO_ASPECT_RATIO
                ? AUTO_ASPECT_RATIO
                : nearestAspectRatio(input.size),
            resolution: input.resolution,
        };
    }
    return { width: input.size.widthPx, height: input.size.heightPx };
}
export function imageModelParams(input) {
    if (usesAspectRatio(input.modelId)) {
        return {
            ...(input.aspectRatio === AUTO_ASPECT_RATIO
                ? {}
                : { aspect_ratio: nearestAspectRatio(input.size) }),
            resolution: input.resolution,
            output_format: input.outputFormat ?? 'png',
        };
    }
    return {
        size: `${input.size.widthPx}*${input.size.heightPx}`,
        seed: Number(input.seed),
    };
}
//# sourceMappingURL=model-params.js.map